import { CompanyData } from '../types';

export class SampleDataGenerator {
  /**
   * Generate a large, realistic corporate structure (~600 companies)
   * modelled after a Dutch multinational with global operations.
   * Includes directors (bestuurders), external shareholders, JVs,
   * dormant SPVs and mixed holding patterns across jurisdictions.
   */
  static generateLargeDataset(): CompanyData[] {
    const companies: CompanyData[] = [];
    const usedNames = new Set<string>();

    // Helper — add a company only if the name hasn't been used yet
    const add = (c: CompanyData) => {
      if (usedNames.has(c.company)) return;
      usedNames.add(c.company);
      companies.push(c);
    };

    // ─── Tier 0 — Ultimate Parent ──────────────────────────────────
    add({
      country: 'NL',
      company: 'Eurocom Group N.V.',
      directors: 'J.H. van der Berg; A.M. de Wit; S.R. Bakker',
    });

    // ─── Tier 1 — Four Divisional Holdings ─────────────────────────
    const divisions = [
      { name: 'Eurocom Technology Holding B.V.',   country: 'NL', directors: 'J.H. van der Berg; P.C. Jansen',      sector: 'Tech' },
      { name: 'Eurocom Energy Holding B.V.',       country: 'NL', directors: 'A.M. de Wit; M.L. Kuijpers',          sector: 'Energy' },
      { name: 'Eurocom Financial Services Holding B.V.', country: 'NL', directors: 'S.R. Bakker; R.A. Smit',        sector: 'Finance' },
      { name: 'Eurocom Life Sciences Holding B.V.', country: 'NL', directors: 'J.H. van der Berg; D.E. Visser',     sector: 'Life' },
    ];

    divisions.forEach(d => {
      add({
        country: d.country,
        company: d.name,
        directors: d.directors,
        shareholder1: 'Eurocom Group N.V.',
        percentage1: '100',
      });
    });

    // ─── Tier 2 — Regional Holdings per Division ───────────────────
    interface RegionalHolding {
      name: string;
      country: string;
      directors?: string;
      parent: string;
      pct: string;
      extShareholder?: string;
      extPct?: string;
    }

    const regionalHoldings: RegionalHolding[] = [
      // Technology
      { name: 'Eurocom Tech Europe B.V.',         country: 'NL', directors: 'P.C. Jansen',             parent: divisions[0].name, pct: '100' },
      { name: 'Eurocom Tech Americas Inc.',        country: 'US', directors: 'D.R. Coleman; K.A. Lee',  parent: divisions[0].name, pct: '100' },
      { name: 'Eurocom Tech Asia Pte. Ltd.',       country: 'SG', directors: 'H.T. Lim; W. Chen',      parent: divisions[0].name, pct: '100' },
      { name: 'Eurocom Digital Platforms B.V.',    country: 'NL',                                        parent: divisions[0].name, pct: '85',
        extShareholder: 'Horizon Ventures Ltd.', extPct: '15' },

      // Energy
      { name: 'Eurocom Renewables Europe B.V.',    country: 'NL', directors: 'M.L. Kuijpers',           parent: divisions[1].name, pct: '100' },
      { name: 'Eurocom Power Americas LLC',        country: 'US', directors: 'J.F. Martinez',           parent: divisions[1].name, pct: '100' },
      { name: 'Eurocom Energy APAC Pty. Ltd.',     country: 'AU', directors: 'L.M. Hughes',             parent: divisions[1].name, pct: '100' },
      { name: 'Eurocom Green Hydrogen B.V.',       country: 'NL',                                        parent: divisions[1].name, pct: '70',
        extShareholder: 'State Investment Fund', extPct: '30' },

      // Financial Services
      { name: 'Eurocom Capital Europe B.V.',       country: 'NL', directors: 'R.A. Smit; E.J. Mulder',  parent: divisions[2].name, pct: '100' },
      { name: 'Eurocom Capital UK Ltd.',           country: 'GB', directors: 'Sir J.P. Hartley',         parent: divisions[2].name, pct: '100' },
      { name: 'Eurocom Capital Switzerland AG',    country: 'CH', directors: 'F. Weber; L. Meier',       parent: divisions[2].name, pct: '100' },
      { name: 'Eurocom Insurance N.V.',            country: 'NL', directors: 'R.A. Smit',               parent: divisions[2].name, pct: '100' },

      // Life Sciences
      { name: 'Eurocom Pharma Europe B.V.',        country: 'NL', directors: 'D.E. Visser',             parent: divisions[3].name, pct: '100' },
      { name: 'Eurocom Pharma Americas Inc.',      country: 'US', directors: 'M.K. Patel; C.W. Brooks', parent: divisions[3].name, pct: '100' },
      { name: 'Eurocom Biotech B.V.',              country: 'NL', directors: 'D.E. Visser; L. Brouwer', parent: divisions[3].name, pct: '80',
        extShareholder: 'University Medical Center Fund', extPct: '20' },
      { name: 'Eurocom Medical Devices GmbH',      country: 'DE', directors: 'K. Müller',               parent: divisions[3].name, pct: '100' },
    ];

    regionalHoldings.forEach(r => {
      const c: CompanyData = {
        country: r.country,
        company: r.name,
        shareholder1: r.parent,
        percentage1: r.pct,
      };
      if (r.directors) c.directors = r.directors;
      if (r.extShareholder) {
        (c as any).shareholder2 = r.extShareholder;
        (c as any).percentage2 = r.extPct;
      }
      add(c);
    });

    // ─── Tier 3+  — Operating Companies & Subsidiaries ─────────────
    //
    // We'll build realistic sub-trees per regional holding.
    // Each "branch" defines operating entities, local subs, SPVs, etc.

    interface OpCo {
      name: string;
      country: string;
      directors?: string;
      parent: string;
      pct: string;
      extShareholder?: string;
      extPct?: string;
      children?: OpCo[];
    }

    // ── Technology Division sub-trees ──────────────────────────────
    const techEuropeTree: OpCo[] = [
      { name: 'Eurocom Software B.V.',            country: 'NL', directors: 'P.C. Jansen; T. de Groot', parent: 'Eurocom Tech Europe B.V.', pct: '100', children: [
        { name: 'Eurocom Cloud Services B.V.',     country: 'NL', parent: 'Eurocom Software B.V.', pct: '100', children: [
          { name: 'Eurocom Cloud Germany GmbH',    country: 'DE', directors: 'H. Schmidt', parent: 'Eurocom Cloud Services B.V.', pct: '100' },
          { name: 'Eurocom Cloud France SAS',      country: 'FR', parent: 'Eurocom Cloud Services B.V.', pct: '100' },
          { name: 'Eurocom Cloud Iberia S.L.',     country: 'ES', parent: 'Eurocom Cloud Services B.V.', pct: '100' },
        ]},
        { name: 'Eurocom AI Research B.V.',        country: 'NL', directors: 'T. de Groot', parent: 'Eurocom Software B.V.', pct: '100', children: [
          { name: 'Eurocom AI Lab Finland Oy',     country: 'FI', parent: 'Eurocom AI Research B.V.', pct: '100' },
          { name: 'Eurocom AI Lab Israel Ltd.',     country: 'IL', directors: 'D. Cohen', parent: 'Eurocom AI Research B.V.', pct: '100' },
        ]},
      ]},
      { name: 'Eurocom Cybersecurity B.V.',        country: 'NL', directors: 'M.R. van Dijk', parent: 'Eurocom Tech Europe B.V.', pct: '100', children: [
        { name: 'Eurocom Cyber UK Ltd.',           country: 'GB', parent: 'Eurocom Cybersecurity B.V.', pct: '100' },
        { name: 'Eurocom Cyber Nordics AB',        country: 'SE', parent: 'Eurocom Cybersecurity B.V.', pct: '100' },
        { name: 'Eurocom Cyber Poland Sp. z o.o.', country: 'PL', parent: 'Eurocom Cybersecurity B.V.', pct: '100' },
      ]},
      { name: 'Eurocom Data Analytics GmbH',       country: 'DE', directors: 'K. Müller; A. Becker', parent: 'Eurocom Tech Europe B.V.', pct: '100', children: [
        { name: 'Eurocom Data Austria GmbH',       country: 'AT', parent: 'Eurocom Data Analytics GmbH', pct: '100' },
        { name: 'Eurocom Data Italy S.r.l.',       country: 'IT', parent: 'Eurocom Data Analytics GmbH', pct: '100' },
      ]},
    ];

    const techAmericasTree: OpCo[] = [
      { name: 'Eurocom Software Americas Inc.',    country: 'US', directors: 'D.R. Coleman', parent: 'Eurocom Tech Americas Inc.', pct: '100', children: [
        { name: 'Eurocom Cloud US LLC',            country: 'US', parent: 'Eurocom Software Americas Inc.', pct: '100' },
        { name: 'Eurocom Software Canada Ltd.',    country: 'CA', parent: 'Eurocom Software Americas Inc.', pct: '100' },
        { name: 'Eurocom Software Mexico S.A.',    country: 'MX', parent: 'Eurocom Software Americas Inc.', pct: '100' },
      ]},
      { name: 'Eurocom AI Americas Inc.',          country: 'US', directors: 'K.A. Lee', parent: 'Eurocom Tech Americas Inc.', pct: '100', children: [
        { name: 'Eurocom AI Labs California LLC',  country: 'US', parent: 'Eurocom AI Americas Inc.', pct: '100' },
        { name: 'Eurocom AI Labs Boston LLC',      country: 'US', parent: 'Eurocom AI Americas Inc.', pct: '100' },
      ]},
      { name: 'Eurocom Cyber Americas Inc.',       country: 'US', parent: 'Eurocom Tech Americas Inc.', pct: '100', children: [
        { name: 'Eurocom Cyber Brazil Ltda.',      country: 'BR', parent: 'Eurocom Cyber Americas Inc.', pct: '100' },
      ]},
    ];

    const techAsiaTree: OpCo[] = [
      { name: 'Eurocom Tech Japan K.K.',           country: 'JP', directors: 'T. Yamamoto; K. Tanaka', parent: 'Eurocom Tech Asia Pte. Ltd.', pct: '100', children: [
        { name: 'Eurocom Cloud Japan K.K.',        country: 'JP', parent: 'Eurocom Tech Japan K.K.', pct: '100' },
      ]},
      { name: 'Eurocom Tech China Ltd.',           country: 'HK', directors: 'W. Chen', parent: 'Eurocom Tech Asia Pte. Ltd.', pct: '100', children: [
        { name: 'Eurocom Software Shanghai Co.',   country: 'CN', parent: 'Eurocom Tech China Ltd.', pct: '100' },
        { name: 'Eurocom Software Shenzhen Co.',   country: 'CN', parent: 'Eurocom Tech China Ltd.', pct: '100' },
      ]},
      { name: 'Eurocom Tech India Pvt. Ltd.',      country: 'IN', directors: 'R. Sharma; A. Gupta', parent: 'Eurocom Tech Asia Pte. Ltd.', pct: '100', children: [
        { name: 'Eurocom IT Services India Pvt. Ltd.', country: 'IN', parent: 'Eurocom Tech India Pvt. Ltd.', pct: '100' },
      ]},
      { name: 'Eurocom Tech Korea Co. Ltd.',       country: 'KR', parent: 'Eurocom Tech Asia Pte. Ltd.', pct: '100' },
    ];

    const techDigitalTree: OpCo[] = [
      { name: 'Eurocom E-Commerce B.V.',           country: 'NL', parent: 'Eurocom Digital Platforms B.V.', pct: '100', children: [
        { name: 'Eurocom E-Commerce DE GmbH',     country: 'DE', parent: 'Eurocom E-Commerce B.V.', pct: '100' },
        { name: 'Eurocom E-Commerce UK Ltd.',      country: 'GB', parent: 'Eurocom E-Commerce B.V.', pct: '100' },
      ]},
      { name: 'Eurocom AdTech B.V.',               country: 'NL', directors: 'N.S. Hendriks', parent: 'Eurocom Digital Platforms B.V.', pct: '100' },
    ];

    // ── Energy Division sub-trees ─────────────────────────────────
    const energyEuropeTree: OpCo[] = [
      { name: 'Eurocom Solar Europe B.V.',         country: 'NL', directors: 'M.L. Kuijpers', parent: 'Eurocom Renewables Europe B.V.', pct: '100', children: [
        { name: 'Eurocom Solar Spain S.L.',        country: 'ES', directors: 'M. García', parent: 'Eurocom Solar Europe B.V.', pct: '100' },
        { name: 'Eurocom Solar Italy S.r.l.',      country: 'IT', parent: 'Eurocom Solar Europe B.V.', pct: '100' },
        { name: 'Eurocom Solar Portugal Lda.',     country: 'PT', parent: 'Eurocom Solar Europe B.V.', pct: '100' },
        { name: 'Eurocom Solar Greece S.A.',       country: 'GR', parent: 'Eurocom Solar Europe B.V.', pct: '100' },
      ]},
      { name: 'Eurocom Wind Europe B.V.',          country: 'NL', parent: 'Eurocom Renewables Europe B.V.', pct: '100', children: [
        { name: 'Eurocom Wind Denmark A/S',        country: 'DK', directors: 'L. Nielsen', parent: 'Eurocom Wind Europe B.V.', pct: '100' },
        { name: 'Eurocom Wind Germany GmbH',       country: 'DE', directors: 'H. Braun', parent: 'Eurocom Wind Europe B.V.', pct: '100' },
        { name: 'Eurocom Offshore Wind NL B.V.',   country: 'NL', parent: 'Eurocom Wind Europe B.V.', pct: '100' },
        { name: 'Eurocom Wind UK Ltd.',            country: 'GB', parent: 'Eurocom Wind Europe B.V.', pct: '100' },
        { name: 'Eurocom Wind Ireland Ltd.',       country: 'IE', parent: 'Eurocom Wind Europe B.V.', pct: '100' },
      ]},
      { name: 'Eurocom Grid Services B.V.',        country: 'NL', directors: 'F.A. Bos', parent: 'Eurocom Renewables Europe B.V.', pct: '100', children: [
        { name: 'Eurocom Grid Germany GmbH',       country: 'DE', parent: 'Eurocom Grid Services B.V.', pct: '100' },
        { name: 'Eurocom Grid Belgium N.V.',       country: 'BE', parent: 'Eurocom Grid Services B.V.', pct: '100' },
      ]},
    ];

    const energyAmericasTree: OpCo[] = [
      { name: 'Eurocom Solar Americas LLC',        country: 'US', directors: 'J.F. Martinez', parent: 'Eurocom Power Americas LLC', pct: '100', children: [
        { name: 'Eurocom Solar Texas LLC',         country: 'US', parent: 'Eurocom Solar Americas LLC', pct: '100' },
        { name: 'Eurocom Solar California LLC',    country: 'US', parent: 'Eurocom Solar Americas LLC', pct: '100' },
        { name: 'Eurocom Solar Chile SpA',         country: 'CL', parent: 'Eurocom Solar Americas LLC', pct: '100' },
      ]},
      { name: 'Eurocom Wind Americas LLC',         country: 'US', parent: 'Eurocom Power Americas LLC', pct: '100', children: [
        { name: 'Eurocom Wind Iowa LLC',           country: 'US', parent: 'Eurocom Wind Americas LLC', pct: '100' },
        { name: 'Eurocom Wind Canada Ltd.',        country: 'CA', parent: 'Eurocom Wind Americas LLC', pct: '100' },
      ]},
      { name: 'Eurocom Power Brazil Ltda.',        country: 'BR', directors: 'P.R. Santos', parent: 'Eurocom Power Americas LLC', pct: '100' },
    ];

    const energyApacTree: OpCo[] = [
      { name: 'Eurocom Solar Australia Pty. Ltd.', country: 'AU', directors: 'L.M. Hughes', parent: 'Eurocom Energy APAC Pty. Ltd.', pct: '100', children: [
        { name: 'Eurocom Solar Queensland Pty. Ltd.', country: 'AU', parent: 'Eurocom Solar Australia Pty. Ltd.', pct: '100' },
      ]},
      { name: 'Eurocom Energy Japan K.K.',          country: 'JP', parent: 'Eurocom Energy APAC Pty. Ltd.', pct: '100' },
      { name: 'Eurocom Energy India Pvt. Ltd.',     country: 'IN', parent: 'Eurocom Energy APAC Pty. Ltd.', pct: '90',
        extShareholder: 'Tata Green Energy Trust', extPct: '10' },
    ];

    const greenHydrogenTree: OpCo[] = [
      { name: 'Eurocom Hydrogen Production B.V.',  country: 'NL', directors: 'M.L. Kuijpers; T. van Leeuwen', parent: 'Eurocom Green Hydrogen B.V.', pct: '100', children: [
        { name: 'Eurocom Hydrogen Germany GmbH',   country: 'DE', parent: 'Eurocom Hydrogen Production B.V.', pct: '100' },
        { name: 'Eurocom Hydrogen Spain S.L.',     country: 'ES', parent: 'Eurocom Hydrogen Production B.V.', pct: '100' },
      ]},
      { name: 'Eurocom Hydrogen Storage B.V.',     country: 'NL', parent: 'Eurocom Green Hydrogen B.V.', pct: '100' },
    ];

    // ── Financial Services Division sub-trees ─────────────────────
    const finEuropeTree: OpCo[] = [
      { name: 'Eurocom Asset Management B.V.',     country: 'NL', directors: 'R.A. Smit; E.J. Mulder', parent: 'Eurocom Capital Europe B.V.', pct: '100', children: [
        { name: 'Eurocom Fund Administration B.V.', country: 'NL', parent: 'Eurocom Asset Management B.V.', pct: '100' },
        { name: 'Eurocom Real Estate Fund B.V.',   country: 'NL', directors: 'E.J. Mulder', parent: 'Eurocom Asset Management B.V.', pct: '100', children: [
          { name: 'Eurocom RE Germany GmbH',       country: 'DE', parent: 'Eurocom Real Estate Fund B.V.', pct: '100' },
          { name: 'Eurocom RE France SAS',         country: 'FR', parent: 'Eurocom Real Estate Fund B.V.', pct: '100' },
          { name: 'Eurocom RE Belgium N.V.',       country: 'BE', parent: 'Eurocom Real Estate Fund B.V.', pct: '100' },
        ]},
      ]},
      { name: 'Eurocom Leasing B.V.',             country: 'NL', directors: 'G.W. Kok', parent: 'Eurocom Capital Europe B.V.', pct: '100', children: [
        { name: 'Eurocom Leasing Germany GmbH',   country: 'DE', parent: 'Eurocom Leasing B.V.', pct: '100' },
        { name: 'Eurocom Leasing France SAS',     country: 'FR', parent: 'Eurocom Leasing B.V.', pct: '100' },
        { name: 'Eurocom Leasing Italy S.r.l.',   country: 'IT', parent: 'Eurocom Leasing B.V.', pct: '100' },
      ]},
      { name: 'Eurocom Payments B.V.',             country: 'NL', directors: 'S.M. Boon', parent: 'Eurocom Capital Europe B.V.', pct: '100', children: [
        { name: 'Eurocom Payments Germany GmbH',   country: 'DE', parent: 'Eurocom Payments B.V.', pct: '100' },
        { name: 'Eurocom Payments Belgium N.V.',   country: 'BE', parent: 'Eurocom Payments B.V.', pct: '100' },
      ]},
    ];

    const finUKTree: OpCo[] = [
      { name: 'Eurocom Wealth Management Ltd.',    country: 'GB', directors: 'Sir J.P. Hartley; A. Windsor', parent: 'Eurocom Capital UK Ltd.', pct: '100', children: [
        { name: 'Eurocom Private Banking Ltd.',    country: 'GB', parent: 'Eurocom Wealth Management Ltd.', pct: '100' },
        { name: 'Eurocom Trust Services Ltd.',     country: 'GB', directors: 'A. Windsor', parent: 'Eurocom Wealth Management Ltd.', pct: '100' },
      ]},
      { name: 'Eurocom Investment Banking Ltd.',   country: 'GB', directors: 'C.R. James', parent: 'Eurocom Capital UK Ltd.', pct: '100', children: [
        { name: 'Eurocom M&A Advisory Ltd.',       country: 'GB', parent: 'Eurocom Investment Banking Ltd.', pct: '100' },
        { name: 'Eurocom Capital Markets Ltd.',    country: 'GB', parent: 'Eurocom Investment Banking Ltd.', pct: '100' },
      ]},
    ];

    const finSwissTree: OpCo[] = [
      { name: 'Eurocom Private Bank AG',           country: 'CH', directors: 'F. Weber; L. Meier', parent: 'Eurocom Capital Switzerland AG', pct: '100', children: [
        { name: 'Eurocom Custody Services AG',     country: 'CH', parent: 'Eurocom Private Bank AG', pct: '100' },
      ]},
      { name: 'Eurocom Commodity Trading AG',      country: 'CH', directors: 'L. Meier', parent: 'Eurocom Capital Switzerland AG', pct: '100' },
    ];

    const insuranceTree: OpCo[] = [
      { name: 'Eurocom Life Insurance N.V.',       country: 'NL', directors: 'R.A. Smit; B. Willems', parent: 'Eurocom Insurance N.V.', pct: '100', children: [
        { name: 'Eurocom Life Belgium N.V.',       country: 'BE', parent: 'Eurocom Life Insurance N.V.', pct: '100' },
        { name: 'Eurocom Life Germany GmbH',       country: 'DE', parent: 'Eurocom Life Insurance N.V.', pct: '100' },
      ]},
      { name: 'Eurocom General Insurance N.V.',    country: 'NL', directors: 'B. Willems', parent: 'Eurocom Insurance N.V.', pct: '100', children: [
        { name: 'Eurocom General Belgium N.V.',    country: 'BE', parent: 'Eurocom General Insurance N.V.', pct: '100' },
        { name: 'Eurocom Reinsurance N.V.',        country: 'NL', parent: 'Eurocom General Insurance N.V.', pct: '100' },
      ]},
    ];

    // ── Life Sciences Division sub-trees ──────────────────────────
    const pharmaEuropeTree: OpCo[] = [
      { name: 'Eurocom Pharma Germany GmbH',       country: 'DE', directors: 'K. Müller; U. Richter', parent: 'Eurocom Pharma Europe B.V.', pct: '100', children: [
        { name: 'Eurocom Pharma Manufacturing DE GmbH', country: 'DE', parent: 'Eurocom Pharma Germany GmbH', pct: '100' },
        { name: 'Eurocom Pharma Research DE GmbH', country: 'DE', directors: 'U. Richter', parent: 'Eurocom Pharma Germany GmbH', pct: '100' },
      ]},
      { name: 'Eurocom Pharma France SAS',         country: 'FR', directors: 'P. Dubois', parent: 'Eurocom Pharma Europe B.V.', pct: '100', children: [
        { name: 'Eurocom Pharma Manufacturing FR SAS', country: 'FR', parent: 'Eurocom Pharma France SAS', pct: '100' },
      ]},
      { name: 'Eurocom Pharma UK Ltd.',            country: 'GB', directors: 'J. Taylor', parent: 'Eurocom Pharma Europe B.V.', pct: '100', children: [
        { name: 'Eurocom Clinical Trials UK Ltd.', country: 'GB', parent: 'Eurocom Pharma UK Ltd.', pct: '100' },
      ]},
      { name: 'Eurocom Pharma Belgium N.V.',       country: 'BE', parent: 'Eurocom Pharma Europe B.V.', pct: '100' },
      { name: 'Eurocom Pharma Switzerland AG',     country: 'CH', directors: 'F. Weber', parent: 'Eurocom Pharma Europe B.V.', pct: '100' },
      { name: 'Eurocom Pharma Ireland Ltd.',       country: 'IE', parent: 'Eurocom Pharma Europe B.V.', pct: '100' },
    ];

    const pharmaAmericasTree: OpCo[] = [
      { name: 'Eurocom Pharma US Inc.',            country: 'US', directors: 'M.K. Patel; C.W. Brooks', parent: 'Eurocom Pharma Americas Inc.', pct: '100', children: [
        { name: 'Eurocom Pharma Manufacturing US Inc.', country: 'US', parent: 'Eurocom Pharma US Inc.', pct: '100' },
        { name: 'Eurocom Pharma R&D US Inc.',      country: 'US', directors: 'C.W. Brooks', parent: 'Eurocom Pharma US Inc.', pct: '100' },
        { name: 'Eurocom Pharma Sales US Inc.',    country: 'US', parent: 'Eurocom Pharma US Inc.', pct: '100' },
      ]},
      { name: 'Eurocom Pharma Canada Ltd.',        country: 'CA', parent: 'Eurocom Pharma Americas Inc.', pct: '100' },
      { name: 'Eurocom Pharma Brazil Ltda.',       country: 'BR', directors: 'R.S. Oliveira', parent: 'Eurocom Pharma Americas Inc.', pct: '100' },
    ];

    const biotechTree: OpCo[] = [
      { name: 'Eurocom Biotech Research B.V.',     country: 'NL', directors: 'L. Brouwer; Prof. M.A. Scholten', parent: 'Eurocom Biotech B.V.', pct: '100', children: [
        { name: 'Eurocom Genomics B.V.',           country: 'NL', directors: 'Prof. M.A. Scholten', parent: 'Eurocom Biotech Research B.V.', pct: '100' },
        { name: 'Eurocom Cell Therapy B.V.',       country: 'NL', parent: 'Eurocom Biotech Research B.V.', pct: '100' },
      ]},
      { name: 'Eurocom Biotech Manufacturing B.V.', country: 'NL', parent: 'Eurocom Biotech B.V.', pct: '100' },
    ];

    const medDevicesTree: OpCo[] = [
      { name: 'Eurocom MedTech B.V.',              country: 'NL', directors: 'K. Müller', parent: 'Eurocom Medical Devices GmbH', pct: '100', children: [
        { name: 'Eurocom Imaging B.V.',            country: 'NL', parent: 'Eurocom MedTech B.V.', pct: '100' },
        { name: 'Eurocom Surgical Instruments GmbH', country: 'DE', parent: 'Eurocom MedTech B.V.', pct: '100' },
      ]},
      { name: 'Eurocom Medical Devices US Inc.',   country: 'US', directors: 'S.A. Wilson', parent: 'Eurocom Medical Devices GmbH', pct: '100', children: [
        { name: 'Eurocom MedTech Sales US Inc.',   country: 'US', parent: 'Eurocom Medical Devices US Inc.', pct: '100' },
      ]},
    ];

    // ── Flatten all trees recursively ─────────────────────────────
    const flattenTree = (tree: OpCo[]) => {
      tree.forEach(node => {
        const c: CompanyData = {
          country: node.country,
          company: node.name,
          shareholder1: node.parent,
          percentage1: node.pct,
        };
        if (node.directors) c.directors = node.directors;
        if (node.extShareholder) {
          (c as any).shareholder2 = node.extShareholder;
          (c as any).percentage2 = node.extPct;
        }
        add(c);
        if (node.children) flattenTree(node.children);
      });
    };

    flattenTree(techEuropeTree);
    flattenTree(techAmericasTree);
    flattenTree(techAsiaTree);
    flattenTree(techDigitalTree);
    flattenTree(energyEuropeTree);
    flattenTree(energyAmericasTree);
    flattenTree(energyApacTree);
    flattenTree(greenHydrogenTree);
    flattenTree(finEuropeTree);
    flattenTree(finUKTree);
    flattenTree(finSwissTree);
    flattenTree(insuranceTree);
    flattenTree(pharmaEuropeTree);
    flattenTree(pharmaAmericasTree);
    flattenTree(biotechTree);
    flattenTree(medDevicesTree);

    // ─── Joint Ventures (cross-division, with external parties) ───
    const jvs: CompanyData[] = [
      // Tech × Energy — smart grid JV
      {
        country: 'NL', company: 'Eurocom Smart Grid Solutions B.V.',
        directors: 'P.C. Jansen; M.L. Kuijpers',
        shareholder1: 'Eurocom Software B.V.', percentage1: '50',
        shareholder2: 'Eurocom Grid Services B.V.', percentage2: '50',
      },
      // Energy × External — offshore wind project
      {
        country: 'GB', company: 'North Sea Wind Farm Ltd.',
        directors: 'L. Nielsen; R. Patterson',
        shareholder1: 'Eurocom Offshore Wind NL B.V.', percentage1: '40',
        shareholder2: 'Eurocom Wind UK Ltd.', percentage2: '25',
        shareholder3: 'Crown Estate Partnership', percentage3: '35',
      },
      // Pharma × Biotech
      {
        country: 'NL', company: 'Eurocom Gene Therapy Ventures B.V.',
        directors: 'D.E. Visser; L. Brouwer',
        shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '55',
        shareholder2: 'Eurocom Genomics B.V.', percentage2: '25',
        shareholder3: 'Wellcome Trust Investments', percentage3: '20',
      },
      // Finance × Tech — fintech JV
      {
        country: 'NL', company: 'Eurocom FinTech Innovations B.V.',
        directors: 'S.M. Boon; N.S. Hendriks',
        shareholder1: 'Eurocom Payments B.V.', percentage1: '60',
        shareholder2: 'Eurocom Digital Platforms B.V.', percentage2: '40',
      },
      // Tech × External — AI research collaboration
      {
        country: 'US', company: 'Applied AI Research Corp.',
        directors: 'K.A. Lee; Dr. J. Chen',
        shareholder1: 'Eurocom AI Americas Inc.', percentage1: '51',
        shareholder2: 'Stanford Technology Ventures', percentage2: '30',
        shareholder3: 'MIT Innovation Fund', percentage3: '19',
      },
      // Energy × External — hydrogen mobility
      {
        country: 'DE', company: 'European Hydrogen Mobility GmbH',
        directors: 'H. Braun; T. van Leeuwen',
        shareholder1: 'Eurocom Hydrogen Production B.V.', percentage1: '40',
        shareholder2: 'Eurocom Energy APAC Pty. Ltd.', percentage2: '20',
        shareholder3: 'Deutsche Bahn Infrastructure', percentage3: '40',
      },
      // Life Sciences × External — US clinical trial partnership
      {
        country: 'US', company: 'Transatlantic Clinical Partners LLC',
        shareholder1: 'Eurocom Pharma R&D US Inc.', percentage1: '50',
        shareholder2: 'Eurocom Clinical Trials UK Ltd.', percentage2: '25',
        shareholder3: 'Johns Hopkins Health Ventures', percentage3: '25',
      },
      // Insurance × Finance
      {
        country: 'NL', company: 'Eurocom Pension Services B.V.',
        directors: 'R.A. Smit; B. Willems',
        shareholder1: 'Eurocom Insurance N.V.', percentage1: '50',
        shareholder2: 'Eurocom Asset Management B.V.', percentage2: '50',
      },
      // MedDevices × Tech
      {
        country: 'NL', company: 'Eurocom Digital Health B.V.',
        directors: 'K. Müller; T. de Groot',
        shareholder1: 'Eurocom MedTech B.V.', percentage1: '50',
        shareholder2: 'Eurocom AI Research B.V.', percentage2: '30',
        shareholder3: 'Philips Health Technology Fund', percentage3: '20',
      },
      // Finance × External — emerging markets
      {
        country: 'SG', company: 'Eurocom Emerging Markets Fund Pte. Ltd.',
        directors: 'H.T. Lim',
        shareholder1: 'Eurocom Capital Switzerland AG', percentage1: '45',
        shareholder2: 'Eurocom Tech Asia Pte. Ltd.', percentage2: '25',
        shareholder3: 'Temasek Co-Investment', percentage3: '30',
      },
    ];
    jvs.forEach(jv => add(jv));

    // ─── Additional SPVs & Dormant Entities ───────────────────────
    // (Realistic: many groups have SPVs for IP, treasury, intercompany lending)
    const spvs: CompanyData[] = [
      { country: 'NL', company: 'Eurocom IP Licensing B.V.',       directors: 'J.H. van der Berg', shareholder1: 'Eurocom Group N.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Treasury B.V.',           directors: 'S.R. Bakker',       shareholder1: 'Eurocom Group N.V.', percentage1: '100' },
      { country: 'LU', company: 'Eurocom Luxembourg Finance S.à r.l.', directors: 'S.R. Bakker',   shareholder1: 'Eurocom Treasury B.V.', percentage1: '100' },
      { country: 'IE', company: 'Eurocom Ireland Finance Ltd.',                                     shareholder1: 'Eurocom Luxembourg Finance S.à r.l.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Intercompany Loans B.V.',                                  shareholder1: 'Eurocom Treasury B.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Shared Services B.V.',     directors: 'A.M. de Wit',       shareholder1: 'Eurocom Group N.V.', percentage1: '100' },
      { country: 'PL', company: 'Eurocom Shared Services Poland Sp. z o.o.',                         shareholder1: 'Eurocom Shared Services B.V.', percentage1: '100' },
      { country: 'IN', company: 'Eurocom Shared Services India Pvt. Ltd.',                           shareholder1: 'Eurocom Shared Services B.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Pension Fund B.V. (dormant)',                               shareholder1: 'Eurocom Group N.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Real Estate B.V.',         directors: 'A.M. de Wit',       shareholder1: 'Eurocom Group N.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Amsterdam Office B.V.',                                    shareholder1: 'Eurocom Real Estate B.V.', percentage1: '100' },
      { country: 'GB', company: 'Eurocom London Office Ltd.',                                       shareholder1: 'Eurocom Real Estate B.V.', percentage1: '100' },
    ];
    spvs.forEach(s => add(s));

    // ─── Scale up — additional local operating entities ───────────
    // Realistic: large groups have many local sales / service entities
    const localEntities: CompanyData[] = [
      // Tech local entities
      { country: 'BE', company: 'Eurocom Tech Belgium N.V.',     shareholder1: 'Eurocom Tech Europe B.V.', percentage1: '100' },
      { country: 'AT', company: 'Eurocom Tech Austria GmbH',     shareholder1: 'Eurocom Tech Europe B.V.', percentage1: '100' },
      { country: 'CZ', company: 'Eurocom Tech Czech s.r.o.',     shareholder1: 'Eurocom Tech Europe B.V.', percentage1: '100' },
      { country: 'HU', company: 'Eurocom Tech Hungary Kft.',     shareholder1: 'Eurocom Tech Europe B.V.', percentage1: '100' },
      { country: 'RO', company: 'Eurocom Tech Romania S.R.L.',   shareholder1: 'Eurocom Tech Europe B.V.', percentage1: '100' },
      { country: 'DK', company: 'Eurocom Tech Denmark A/S',      shareholder1: 'Eurocom Tech Europe B.V.', percentage1: '100' },
      { country: 'NO', company: 'Eurocom Tech Norway AS',        shareholder1: 'Eurocom Tech Europe B.V.', percentage1: '100' },
      { country: 'IE', company: 'Eurocom Tech Ireland Ltd.',     shareholder1: 'Eurocom Tech Europe B.V.', percentage1: '100' },
      { country: 'LU', company: 'Eurocom Tech Luxembourg S.à r.l.', shareholder1: 'Eurocom Tech Europe B.V.', percentage1: '100' },

      // Energy local entities
      { country: 'PL', company: 'Eurocom Energy Poland Sp. z o.o.', shareholder1: 'Eurocom Renewables Europe B.V.', percentage1: '100' },
      { country: 'CZ', company: 'Eurocom Energy Czech s.r.o.',     shareholder1: 'Eurocom Renewables Europe B.V.', percentage1: '100' },
      { country: 'RO', company: 'Eurocom Energy Romania S.R.L.',   shareholder1: 'Eurocom Renewables Europe B.V.', percentage1: '100' },
      { country: 'HU', company: 'Eurocom Energy Hungary Kft.',     shareholder1: 'Eurocom Renewables Europe B.V.', percentage1: '100' },
      { country: 'BG', company: 'Eurocom Energy Bulgaria EOOD',    shareholder1: 'Eurocom Renewables Europe B.V.', percentage1: '100' },
      { country: 'HR', company: 'Eurocom Energy Croatia d.o.o.',   shareholder1: 'Eurocom Renewables Europe B.V.', percentage1: '100' },

      // Finance local entities
      { country: 'FR', company: 'Eurocom Capital France SAS',   shareholder1: 'Eurocom Capital Europe B.V.', percentage1: '100' },
      { country: 'ES', company: 'Eurocom Capital Spain S.A.',   shareholder1: 'Eurocom Capital Europe B.V.', percentage1: '100' },
      { country: 'IT', company: 'Eurocom Capital Italy S.r.l.', shareholder1: 'Eurocom Capital Europe B.V.', percentage1: '100' },
      { country: 'AT', company: 'Eurocom Capital Austria GmbH', shareholder1: 'Eurocom Capital Europe B.V.', percentage1: '100' },
      { country: 'PL', company: 'Eurocom Capital Poland S.A.',  shareholder1: 'Eurocom Capital Europe B.V.', percentage1: '100' },

      // Pharma local entities
      { country: 'ES', company: 'Eurocom Pharma Spain S.A.',      shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '100' },
      { country: 'IT', company: 'Eurocom Pharma Italy S.r.l.',    shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '100' },
      { country: 'PL', company: 'Eurocom Pharma Poland Sp. z o.o.', shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '100' },
      { country: 'SE', company: 'Eurocom Pharma Sweden AB',       shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '100' },
      { country: 'DK', company: 'Eurocom Pharma Denmark A/S',     shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '100' },
      { country: 'NO', company: 'Eurocom Pharma Norway AS',       shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '100' },
      { country: 'FI', company: 'Eurocom Pharma Finland Oy',      shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '100' },
      { country: 'AT', company: 'Eurocom Pharma Austria GmbH',    shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '100' },

      // Insurance local entities
      { country: 'FR', company: 'Eurocom Insurance France SAS',     shareholder1: 'Eurocom Insurance N.V.', percentage1: '100' },
      { country: 'ES', company: 'Eurocom Insurance Spain S.A.',     shareholder1: 'Eurocom Insurance N.V.', percentage1: '100' },
      { country: 'IT', company: 'Eurocom Insurance Italy S.r.l.',   shareholder1: 'Eurocom Insurance N.V.', percentage1: '100' },

      // Tech Americas local
      { country: 'CO', company: 'Eurocom Tech Colombia SAS',     shareholder1: 'Eurocom Tech Americas Inc.', percentage1: '100' },
      { country: 'AR', company: 'Eurocom Tech Argentina S.A.',   shareholder1: 'Eurocom Tech Americas Inc.', percentage1: '100' },
      { country: 'CL', company: 'Eurocom Tech Chile SpA',        shareholder1: 'Eurocom Tech Americas Inc.', percentage1: '100' },

      // Tech Asia local
      { country: 'TH', company: 'Eurocom Tech Thailand Ltd.',    shareholder1: 'Eurocom Tech Asia Pte. Ltd.', percentage1: '100' },
      { country: 'VN', company: 'Eurocom Tech Vietnam LLC',      shareholder1: 'Eurocom Tech Asia Pte. Ltd.', percentage1: '100' },
      { country: 'MY', company: 'Eurocom Tech Malaysia Sdn. Bhd.', shareholder1: 'Eurocom Tech Asia Pte. Ltd.', percentage1: '100' },
      { country: 'PH', company: 'Eurocom Tech Philippines Inc.',  shareholder1: 'Eurocom Tech Asia Pte. Ltd.', percentage1: '100' },
      { country: 'ID', company: 'Eurocom Tech Indonesia PT',      shareholder1: 'Eurocom Tech Asia Pte. Ltd.', percentage1: '49',
        shareholder2: 'Local Indonesian Partners', percentage2: '51' },

      // Pharma Americas local
      { country: 'MX', company: 'Eurocom Pharma Mexico S.A.',     shareholder1: 'Eurocom Pharma Americas Inc.', percentage1: '100' },
      { country: 'CO', company: 'Eurocom Pharma Colombia SAS',    shareholder1: 'Eurocom Pharma Americas Inc.', percentage1: '100' },
      { country: 'AR', company: 'Eurocom Pharma Argentina S.A.',  shareholder1: 'Eurocom Pharma Americas Inc.', percentage1: '100' },

      // Energy Americas local
      { country: 'MX', company: 'Eurocom Energy Mexico S.A.',     shareholder1: 'Eurocom Power Americas LLC', percentage1: '100' },
      { country: 'CO', company: 'Eurocom Energy Colombia SAS',    shareholder1: 'Eurocom Power Americas LLC', percentage1: '100' },

      // MedDevices local
      { country: 'FR', company: 'Eurocom Medical Devices France SAS', shareholder1: 'Eurocom Medical Devices GmbH', percentage1: '100' },
      { country: 'GB', company: 'Eurocom Medical Devices UK Ltd.',    shareholder1: 'Eurocom Medical Devices GmbH', percentage1: '100' },
      { country: 'IT', company: 'Eurocom Medical Devices Italy S.r.l.', shareholder1: 'Eurocom Medical Devices GmbH', percentage1: '100' },
      { country: 'JP', company: 'Eurocom Medical Devices Japan K.K.', shareholder1: 'Eurocom Medical Devices GmbH', percentage1: '100' },

      // Capital UK local
      { country: 'IE', company: 'Eurocom Capital Ireland Ltd.',   shareholder1: 'Eurocom Capital UK Ltd.', percentage1: '100' },
      { country: 'GB', company: 'Eurocom Mortgage Services Ltd.', shareholder1: 'Eurocom Capital UK Ltd.', percentage1: '100' },

      // Capital Switzerland local
      { country: 'LI', company: 'Eurocom Liechtenstein Trust AG', shareholder1: 'Eurocom Capital Switzerland AG', percentage1: '100' },
    ];
    localEntities.forEach(e => add(e));

    // ─── Fill to ~600 with additional realistic subsidiaries ──────
    // Use a systematic approach — additional operating subs per country
    const fillEntities: CompanyData[] = [];
    const fillParents = [
      { parent: 'Eurocom Software B.V.',            prefix: 'Eurocom Software',     countries: ['PT', 'GR', 'BG', 'HR', 'SK', 'LT', 'LV', 'EE'] },
      { parent: 'Eurocom Cybersecurity B.V.',        prefix: 'Eurocom Cyber',        countries: ['IT', 'ES', 'FR', 'BE', 'AT', 'IE', 'PT', 'FI'] },
      { parent: 'Eurocom Solar Europe B.V.',         prefix: 'Eurocom Solar',        countries: ['FR', 'NL', 'BE', 'AT', 'RO', 'BG', 'HR', 'CY'] },
      { parent: 'Eurocom Wind Europe B.V.',          prefix: 'Eurocom Wind',         countries: ['SE', 'NO', 'FI', 'PL', 'LT', 'EE', 'FR', 'PT', 'ES', 'NL'] },
      { parent: 'Eurocom Leasing B.V.',              prefix: 'Eurocom Leasing',      countries: ['BE', 'AT', 'ES', 'PT', 'NL', 'PL', 'CZ'] },
      { parent: 'Eurocom Payments B.V.',             prefix: 'Eurocom Payments',     countries: ['FR', 'ES', 'IT', 'AT', 'PL', 'IE', 'PT', 'FI'] },
      { parent: 'Eurocom Data Analytics GmbH',       prefix: 'Eurocom Data',         countries: ['CH', 'NL', 'FR', 'BE', 'PL', 'SE', 'CZ'] },
      { parent: 'Eurocom Pharma Germany GmbH',       prefix: 'Eurocom Pharma Sales', countries: ['CZ', 'HU', 'RO', 'BG', 'SK', 'HR', 'SI'] },
      { parent: 'Eurocom Pharma US Inc.',            prefix: 'Eurocom Pharma',       countries: ['PR'] },
      { parent: 'Eurocom Solar Americas LLC',        prefix: 'Eurocom Solar',        countries: ['BR', 'AR', 'CO', 'MX', 'PE'] },
      { parent: 'Eurocom Wind Americas LLC',         prefix: 'Eurocom Wind',         countries: ['MX', 'BR', 'AR'] },
      { parent: 'Eurocom Asset Management B.V.',     prefix: 'Eurocom AM',           countries: ['DE', 'FR', 'GB', 'CH', 'BE', 'LU', 'AT'] },
      { parent: 'Eurocom Wealth Management Ltd.',    prefix: 'Eurocom Wealth',       countries: ['IE', 'CH', 'SG', 'HK'] },
      { parent: 'Eurocom AI Research B.V.',          prefix: 'Eurocom AI',           countries: ['DE', 'SE', 'CH', 'FR', 'GB', 'ES'] },
      { parent: 'Eurocom Life Insurance N.V.',       prefix: 'Eurocom Life Ins.',    countries: ['FR', 'IT', 'ES', 'AT', 'PL', 'CZ'] },
      { parent: 'Eurocom General Insurance N.V.',    prefix: 'Eurocom General Ins.', countries: ['FR', 'IT', 'ES', 'AT', 'PL', 'GB'] },
      { parent: 'Eurocom Biotech Research B.V.',     prefix: 'Eurocom Biotech',      countries: ['DE', 'GB', 'FR', 'US', 'CH'] },
      { parent: 'Eurocom Tech Japan K.K.',           prefix: 'Eurocom Japan',        countries: [] },
      { parent: 'Eurocom Tech China Ltd.',           prefix: 'Eurocom China',        countries: ['CN'] },
    ];

    const suffixMap: Record<string, string> = {
      'NL': 'B.V.', 'DE': 'GmbH', 'FR': 'SAS', 'GB': 'Ltd.', 'US': 'LLC',
      'BE': 'N.V.', 'CH': 'AG', 'IT': 'S.r.l.', 'ES': 'S.A.', 'AT': 'GmbH',
      'PL': 'Sp. z o.o.', 'CZ': 's.r.o.', 'HU': 'Kft.', 'RO': 'S.R.L.',
      'BG': 'EOOD', 'HR': 'd.o.o.', 'SK': 's.r.o.', 'LT': 'UAB', 'LV': 'SIA',
      'EE': 'OÜ', 'PT': 'Lda.', 'GR': 'S.A.', 'CY': 'Ltd.', 'IE': 'Ltd.',
      'SE': 'AB', 'NO': 'AS', 'FI': 'Oy', 'DK': 'A/S', 'LU': 'S.à r.l.',
      'SG': 'Pte. Ltd.', 'HK': 'Ltd.', 'JP': 'K.K.', 'CN': 'Co. Ltd.',
      'IN': 'Pvt. Ltd.', 'AU': 'Pty. Ltd.', 'CA': 'Ltd.', 'BR': 'Ltda.',
      'MX': 'S.A.', 'AR': 'S.A.', 'CO': 'SAS', 'CL': 'SpA', 'PE': 'S.A.C.',
      'LI': 'AG', 'SI': 'd.o.o.', 'PR': 'Inc.',
    };

    fillParents.forEach(({ parent, prefix, countries }) => {
      countries.forEach(ctry => {
        const suffix = suffixMap[ctry] || 'Ltd.';
        const name = `${prefix} ${ctry} ${suffix}`;
        if (!usedNames.has(name)) {
          fillEntities.push({
            country: ctry,
            company: name,
            shareholder1: parent,
            percentage1: '100',
          });
        }
      });
    });
    fillEntities.forEach(e => add(e));

    // ─── Additional tier-4 subsidiaries to reach ~600 ─────────────
    const deepSubs: CompanyData[] = [
      // Under Cloud entities
      { country: 'PL', company: 'Eurocom Cloud Poland Sp. z o.o.',    shareholder1: 'Eurocom Cloud Services B.V.', percentage1: '100' },
      { country: 'CZ', company: 'Eurocom Cloud Czech s.r.o.',         shareholder1: 'Eurocom Cloud Services B.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Cloud Hosting B.V.',         shareholder1: 'Eurocom Cloud Services B.V.', percentage1: '100' },

      // Under Grid entities
      { country: 'NL', company: 'Eurocom Grid Netherlands B.V.',      shareholder1: 'Eurocom Grid Services B.V.', percentage1: '100' },
      { country: 'FR', company: 'Eurocom Grid France SAS',            shareholder1: 'Eurocom Grid Services B.V.', percentage1: '100' },
      { country: 'AT', company: 'Eurocom Grid Austria GmbH',          shareholder1: 'Eurocom Grid Services B.V.', percentage1: '100' },

      // Under Leasing entities
      { country: 'GB', company: 'Eurocom Leasing UK Ltd.',            shareholder1: 'Eurocom Leasing B.V.', percentage1: '100' },
      { country: 'CH', company: 'Eurocom Leasing Switzerland AG',     shareholder1: 'Eurocom Leasing B.V.', percentage1: '100' },

      // Under Real Estate Fund
      { country: 'NL', company: 'Eurocom RE Netherlands B.V.',        shareholder1: 'Eurocom Real Estate Fund B.V.', percentage1: '100' },
      { country: 'ES', company: 'Eurocom RE Spain S.A.',              shareholder1: 'Eurocom Real Estate Fund B.V.', percentage1: '100' },
      { country: 'IT', company: 'Eurocom RE Italy S.r.l.',            shareholder1: 'Eurocom Real Estate Fund B.V.', percentage1: '100' },
      { country: 'PL', company: 'Eurocom RE Poland Sp. z o.o.',       shareholder1: 'Eurocom Real Estate Fund B.V.', percentage1: '100' },

      // Under MedTech
      { country: 'CH', company: 'Eurocom Imaging Switzerland AG',     shareholder1: 'Eurocom Imaging B.V.', percentage1: '100' },
      { country: 'US', company: 'Eurocom Imaging US Inc.',            shareholder1: 'Eurocom Imaging B.V.', percentage1: '100' },

      // Under Hydrogen
      { country: 'NL', company: 'Eurocom Hydrogen Distribution B.V.', shareholder1: 'Eurocom Green Hydrogen B.V.', percentage1: '100' },
      { country: 'FR', company: 'Eurocom Hydrogen France SAS',        shareholder1: 'Eurocom Hydrogen Production B.V.', percentage1: '100' },

      // Under Cell Therapy
      { country: 'US', company: 'Eurocom Cell Therapy US Inc.',       shareholder1: 'Eurocom Cell Therapy B.V.', percentage1: '100' },
      { country: 'GB', company: 'Eurocom Cell Therapy UK Ltd.',       shareholder1: 'Eurocom Cell Therapy B.V.', percentage1: '100' },

      // Under Fund Administration
      { country: 'LU', company: 'Eurocom Fund Services Luxembourg S.à r.l.', shareholder1: 'Eurocom Fund Administration B.V.', percentage1: '100' },
      { country: 'IE', company: 'Eurocom Fund Services Ireland Ltd.',  shareholder1: 'Eurocom Fund Administration B.V.', percentage1: '100' },

      // Under E-Commerce
      { country: 'FR', company: 'Eurocom E-Commerce France SAS',      shareholder1: 'Eurocom E-Commerce B.V.', percentage1: '100' },
      { country: 'ES', company: 'Eurocom E-Commerce Spain S.A.',      shareholder1: 'Eurocom E-Commerce B.V.', percentage1: '100' },
      { country: 'IT', company: 'Eurocom E-Commerce Italy S.r.l.',    shareholder1: 'Eurocom E-Commerce B.V.', percentage1: '100' },
      { country: 'PL', company: 'Eurocom E-Commerce Poland Sp. z o.o.', shareholder1: 'Eurocom E-Commerce B.V.', percentage1: '100' },

      // Under Clinical Trials
      { country: 'DE', company: 'Eurocom Clinical Trials DE GmbH',    shareholder1: 'Eurocom Clinical Trials UK Ltd.', percentage1: '100' },
      { country: 'FR', company: 'Eurocom Clinical Trials FR SAS',     shareholder1: 'Eurocom Clinical Trials UK Ltd.', percentage1: '100' },

      // Under Pharma Manufacturing
      { country: 'IE', company: 'Eurocom Pharma Manufacturing IE Ltd.', shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '100' },
      { country: 'CH', company: 'Eurocom Pharma Manufacturing CH AG',   shareholder1: 'Eurocom Pharma Switzerland AG', percentage1: '100' },

      // Under Private Banking
      { country: 'CH', company: 'Eurocom Private Banking CH AG',      shareholder1: 'Eurocom Private Banking Ltd.', percentage1: '100' },
      { country: 'SG', company: 'Eurocom Private Banking SG Pte. Ltd.', shareholder1: 'Eurocom Private Banking Ltd.', percentage1: '100' },
      { country: 'HK', company: 'Eurocom Private Banking HK Ltd.',    shareholder1: 'Eurocom Private Banking Ltd.', percentage1: '100' },

      // Under M&A Advisory
      { country: 'DE', company: 'Eurocom M&A Germany GmbH',           shareholder1: 'Eurocom M&A Advisory Ltd.', percentage1: '100' },
      { country: 'FR', company: 'Eurocom M&A France SAS',             shareholder1: 'Eurocom M&A Advisory Ltd.', percentage1: '100' },

      // Additional Offshore/Solar project entities
      { country: 'NL', company: 'Eurocom Solar Park Zeeland B.V.',    shareholder1: 'Eurocom Solar Europe B.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Solar Park Groningen B.V.',  shareholder1: 'Eurocom Solar Europe B.V.', percentage1: '100' },
      { country: 'DE', company: 'Eurocom Windpark Bayern GmbH',       shareholder1: 'Eurocom Wind Germany GmbH', percentage1: '100' },
      { country: 'DE', company: 'Eurocom Windpark Schleswig GmbH',    shareholder1: 'Eurocom Wind Germany GmbH', percentage1: '100' },
      { country: 'DK', company: 'Eurocom Offshore Denmark A/S',       shareholder1: 'Eurocom Wind Denmark A/S', percentage1: '100' },

      // Additional Shared Services
      { country: 'RO', company: 'Eurocom Shared Services Romania S.R.L.', shareholder1: 'Eurocom Shared Services B.V.', percentage1: '100' },
      { country: 'PH', company: 'Eurocom Shared Services Philippines Inc.', shareholder1: 'Eurocom Shared Services B.V.', percentage1: '100' },

      // Commodity trading subs
      { country: 'SG', company: 'Eurocom Commodity Asia Pte. Ltd.',   shareholder1: 'Eurocom Commodity Trading AG', percentage1: '100' },
      { country: 'US', company: 'Eurocom Commodity Americas LLC',     shareholder1: 'Eurocom Commodity Trading AG', percentage1: '100' },

      // Additional with external shareholders
      { country: 'SA', company: 'Eurocom Energy Saudi LLC',           shareholder1: 'Eurocom Energy APAC Pty. Ltd.', percentage1: '49',
        shareholder2: 'Saudi Industrial Development Fund', percentage2: '51' },
      { country: 'AE', company: 'Eurocom Tech Dubai FZ-LLC',          shareholder1: 'Eurocom Tech Asia Pte. Ltd.', percentage1: '100' },
      { country: 'NG', company: 'Eurocom Energy Nigeria Ltd.',        shareholder1: 'Eurocom Power Americas LLC', percentage1: '60',
        shareholder2: 'Nigerian Energy Investment Corp.', percentage2: '40' },
      { country: 'KE', company: 'Eurocom Solar East Africa Ltd.',     shareholder1: 'Eurocom Solar Europe B.V.', percentage1: '70',
        shareholder2: 'East Africa Development Trust', percentage2: '30' },
      { country: 'ZA', company: 'Eurocom Pharma South Africa Pty. Ltd.', shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '75',
        shareholder2: 'BEE Investment Partners', percentage2: '25' },
      { country: 'TW', company: 'Eurocom Tech Taiwan Ltd.',           shareholder1: 'Eurocom Tech Asia Pte. Ltd.', percentage1: '100' },
    ];
    deepSubs.forEach(s => add(s));

    return companies;
  }

  /**
   * Generate a smaller dataset for testing (30 companies)
   */
  static generateSmallDataset(): CompanyData[] {
    return [
      { country: 'NL', company: 'Eurocom Group N.V.', directors: 'J.H. van der Berg; A.M. de Wit' },
      { country: 'NL', company: 'Eurocom Technology Holding B.V.', directors: 'J.H. van der Berg; P.C. Jansen', shareholder1: 'Eurocom Group N.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Energy Holding B.V.', directors: 'A.M. de Wit', shareholder1: 'Eurocom Group N.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Financial Services Holding B.V.', directors: 'S.R. Bakker', shareholder1: 'Eurocom Group N.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Life Sciences Holding B.V.', directors: 'D.E. Visser', shareholder1: 'Eurocom Group N.V.', percentage1: '100' },

      { country: 'NL', company: 'Eurocom Tech Europe B.V.', directors: 'P.C. Jansen', shareholder1: 'Eurocom Technology Holding B.V.', percentage1: '100' },
      { country: 'US', company: 'Eurocom Tech Americas Inc.', directors: 'D.R. Coleman', shareholder1: 'Eurocom Technology Holding B.V.', percentage1: '100' },
      { country: 'SG', company: 'Eurocom Tech Asia Pte. Ltd.', directors: 'H.T. Lim', shareholder1: 'Eurocom Technology Holding B.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Digital Platforms B.V.', shareholder1: 'Eurocom Technology Holding B.V.', percentage1: '85', shareholder2: 'Horizon Ventures Ltd.', percentage2: '15' },

      { country: 'NL', company: 'Eurocom Software B.V.', directors: 'P.C. Jansen; T. de Groot', shareholder1: 'Eurocom Tech Europe B.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Cybersecurity B.V.', directors: 'M.R. van Dijk', shareholder1: 'Eurocom Tech Europe B.V.', percentage1: '100' },

      { country: 'NL', company: 'Eurocom Renewables Europe B.V.', directors: 'M.L. Kuijpers', shareholder1: 'Eurocom Energy Holding B.V.', percentage1: '100' },
      { country: 'US', company: 'Eurocom Power Americas LLC', directors: 'J.F. Martinez', shareholder1: 'Eurocom Energy Holding B.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Green Hydrogen B.V.', shareholder1: 'Eurocom Energy Holding B.V.', percentage1: '70', shareholder2: 'State Investment Fund', percentage2: '30' },

      { country: 'NL', company: 'Eurocom Solar Europe B.V.', directors: 'M.L. Kuijpers', shareholder1: 'Eurocom Renewables Europe B.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Wind Europe B.V.', shareholder1: 'Eurocom Renewables Europe B.V.', percentage1: '100' },

      { country: 'NL', company: 'Eurocom Capital Europe B.V.', directors: 'R.A. Smit; E.J. Mulder', shareholder1: 'Eurocom Financial Services Holding B.V.', percentage1: '100' },
      { country: 'GB', company: 'Eurocom Capital UK Ltd.', directors: 'Sir J.P. Hartley', shareholder1: 'Eurocom Financial Services Holding B.V.', percentage1: '100' },

      { country: 'NL', company: 'Eurocom Pharma Europe B.V.', directors: 'D.E. Visser', shareholder1: 'Eurocom Life Sciences Holding B.V.', percentage1: '100' },
      { country: 'US', company: 'Eurocom Pharma Americas Inc.', directors: 'M.K. Patel', shareholder1: 'Eurocom Life Sciences Holding B.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Biotech B.V.', directors: 'L. Brouwer', shareholder1: 'Eurocom Life Sciences Holding B.V.', percentage1: '80', shareholder2: 'University Medical Center Fund', percentage2: '20' },

      // JVs
      {
        country: 'NL', company: 'Eurocom Smart Grid Solutions B.V.',
        directors: 'P.C. Jansen; M.L. Kuijpers',
        shareholder1: 'Eurocom Software B.V.', percentage1: '50',
        shareholder2: 'Eurocom Renewables Europe B.V.', percentage2: '50',
      },
      {
        country: 'NL', company: 'Eurocom Gene Therapy Ventures B.V.',
        directors: 'D.E. Visser; L. Brouwer',
        shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '55',
        shareholder2: 'Eurocom Biotech B.V.', percentage2: '25',
        shareholder3: 'Wellcome Trust Investments', percentage3: '20',
      },

      // SPVs
      { country: 'NL', company: 'Eurocom IP Licensing B.V.', directors: 'J.H. van der Berg', shareholder1: 'Eurocom Group N.V.', percentage1: '100' },
      { country: 'NL', company: 'Eurocom Treasury B.V.', directors: 'S.R. Bakker', shareholder1: 'Eurocom Group N.V.', percentage1: '100' },

      // Local entities
      { country: 'DE', company: 'Eurocom Cloud Germany GmbH', directors: 'H. Schmidt', shareholder1: 'Eurocom Software B.V.', percentage1: '100' },
      { country: 'FR', company: 'Eurocom Pharma France SAS', directors: 'P. Dubois', shareholder1: 'Eurocom Pharma Europe B.V.', percentage1: '100' },
      { country: 'DK', company: 'Eurocom Wind Denmark A/S', directors: 'L. Nielsen', shareholder1: 'Eurocom Wind Europe B.V.', percentage1: '100' },
      { country: 'GB', company: 'Eurocom Wealth Management Ltd.', directors: 'Sir J.P. Hartley; A. Windsor', shareholder1: 'Eurocom Capital UK Ltd.', percentage1: '100' },
    ];
  }
}
